import { useState } from "react";
import type { PublicPhoto } from "../api/photos";
import { buildPhotoFilename, extensionFromUrl } from "../utils/downloadFilename";
import { savePhotos } from "../utils/savePhotos";
import styles from "./Lightbox.module.css";

interface LightboxProps {
  photos: PublicPhoto[];
  index: number;
  coupleNames: string;
  weddingDate: string;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onSavingChange?: (saving: boolean) => void;
}

export function Lightbox({ photos, index, coupleNames, weddingDate, onClose, onNavigate, onSavingChange }: LightboxProps) {
  const photo = photos[index];
  const [saving, setSaving] = useState(false);
  if (!photo) return null;

  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;
  // photos is newest-first; chronological (upload order) numbering runs oldest -> 1.
  const chronologicalIndex = photos.length - index;

  async function handleSave() {
    setSaving(true);
    onSavingChange?.(true);
    try {
      const filename = buildPhotoFilename(coupleNames, weddingDate, chronologicalIndex, extensionFromUrl(photo!.originalUrl));
      await savePhotos([{ url: photo!.originalUrl, filename }]);
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <button className={styles.closeButton} onClick={onClose} aria-label="Fermer">
        ✕
      </button>
      <button
        className={styles.downloadButton}
        onClick={(e) => {
          e.stopPropagation();
          void handleSave();
        }}
        disabled={saving}
        aria-label="Enregistrer cette photo"
        title="Enregistrer"
      >
        {saving ? "…" : "⬇️"}
      </button>
      {hasPrev && (
        <button
          className={`${styles.navButton} ${styles.prevButton}`}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index - 1);
          }}
          aria-label="Photo précédente"
        >
          ‹
        </button>
      )}
      {hasNext && (
        <button
          className={`${styles.navButton} ${styles.nextButton}`}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index + 1);
          }}
          aria-label="Photo suivante"
        >
          ›
        </button>
      )}
      {photo.mediaType === "video" ? (
        <video
          className={styles.image}
          src={photo.originalUrl}
          controls
          playsInline
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          className={styles.image}
          src={photo.mediumUrl}
          alt=""
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {photo.uploaderName && <p className={styles.caption}>Par {photo.uploaderName}</p>}
    </div>
  );
}
